/***
*
*   submitForm
*   The public form page's submit flow, run from the survey's onComplete.
*
*   The page waits for the POST and acts on its result:
*   - success: delete the drafts, then follow the server redirect, or let
*     SurveyJS navigate (navigateToUrl) or show the thank-you page.
*   - a 4xx or no response: put the survey back on the page where the client
*     pressed Complete, with the answers, and show a message above it.
*   - a 5xx: the server has usually stored the response, so the form stays
*     closed; delete the drafts and show only the "sent" message.
*   submitFailureOutcome (submit-outcome.js) picks reopen or closed and the
*   message.
*
*   showSaveInProgress() runs before the first await: it stops SurveyJS from
*   navigating to navigateToUrl until the POST succeeds. It also starts a
*   SurveyJS "being saved" toast, which is hidden at once: the overlay says
*   it, and the toast would stay on the form after a reopen. showSaveSuccess()
*   and showSaveError() are never called: the first adds a SurveyJS "saved"
*   toast, and the second leaves the survey completed with a "Try again"
*   button that completes again without validation.
*
*   Every side effect is injected, so jest drives it with a real Model:
*
*   sender, options - the onComplete arguments
*   schema       - the served form schema (abort rule, field titles)
*   responseId   - the persistent response id (file keys)
*   guard        - a ref; true while a submit runs, released only on a reopen
*   post         - (answers) => Promise of the axios response
*   clearDrafts  - () => Promise; a failure here never changes the outcome
*   redirect     - (value) => whether it navigated (followSafeRedirect)
*   abortRedirect - (abort, answers) => whether it fired (followAbortRedirect)
*   onSubmitting - (boolean) => shows or hides the "Submitting" overlay
*   onOutcome    - ({ kind, message, fieldLines }) => sets the page message
*   pageWindow   - the window; a back-button restore fires its pageshow
*
**********/

import { followAbortRedirect, followSafeRedirect } from "./safe-redirect";
import { submitFailureOutcome } from "./submit-outcome";

export const OUTCOME_SUCCESS = "success";
export const OUTCOME_REOPEN = "reopen";
export const OUTCOME_CLOSED = "closed";

export async function submitForm(sender, options, deps) {
  const {
    schema,
    responseId,
    guard,
    post,
    clearDrafts,
    redirect = followSafeRedirect,
    abortRedirect = followAbortRedirect,
    onSubmitting = () => {},
    onOutcome = () => {},
    pageWindow = window,
  } = deps;

  if (guard.current) return;
  guard.current = true;

  const answers = buildSubmitAnswers(sender, responseId);

  // The abort redirect is firm-written form JSON: only an absolute http(s)
  // URL is followed, else /finish (stored XSS guard). It never posts.
  if (abortRedirect(schema?.abort, answers)) return;

  options.showSaveInProgress();
  hideSaveToast(sender);
  onSubmitting(true);

  let response;
  try {
    response = await post(answers);
  } catch (err) {
    options.clearSaveMessages();
    const { reopen, message, fieldLines } = submitFailureOutcome(err, schema);

    if (reopen) {
      // Restore before releasing the guard, as public-intake-start.js does.
      restoreSurveyForRetry(sender);
      onOutcome({ kind: OUTCOME_REOPEN, message, fieldLines });
      onSubmitting(false);
      guard.current = false;
      return;
    }

    // The guard stays set: the form is closed for good. The message shows
    // first, so a stalled draft delete cannot hold it back.
    onOutcome({ kind: OUTCOME_CLOSED, message, fieldLines });
    onSubmitting(false);
    await clearDraftsSafely(clearDrafts);
    return;
  }

  options.clearSaveMessages();
  await clearDraftsSafely(clearDrafts);

  // Only an absolute http(s) redirect is followed (stored XSS guard). The
  // page is leaving, so the overlay stays up. The back button can bring the
  // page back from the bfcache as it was left; drop the overlay then, so the
  // completed page shows. The guard stays set: the answers are stored.
  if (redirect(response?.data?.redirect)) {
    onPageRestore(pageWindow, () => onSubmitting(false));
    return;
  }

  sender.navigateTo();
  onOutcome({ kind: OUTCOME_SUCCESS, message: null, fieldLines: [] });
  onSubmitting(false);
}

/*
 * buildSubmitAnswers
 * The POST body: each answer that belongs to a question, by name. A file
 * question's items carry their storage key, responseId/name/file name.
 */
export function buildSubmitAnswers(survey, responseId) {
  const outputObject = {};

  for (const key in survey.data) {
    const question = survey.getQuestionByName(key);
    if (!question) continue;

    // ToDo: handle case where 2 files of the same name are uploaded for the same question (rare case)
    // A URL query param can fill a file question with a string; send that as is.
    if (question.getType() === "file" && Array.isArray(question.value)) {
      outputObject[key] = question.value.map((fileItem) => ({
        ...fileItem,
        key: `${responseId}/${key}/${fileItem.name}`,
      }));
    } else {
      outputObject[key] = question.value;
    }
  }

  return outputObject;
}

/*
 * restoreSurveyForRetry
 * clear(false, false) keeps the answers and the current page, which is the
 * page where the client pressed Complete. On a firstPageIsStarted survey it
 * also puts back the start page; leave that state directly, not with start(),
 * which validates the start page again, starts the timer and fires onStarted.
 */
export function restoreSurveyForRetry(survey) {
  survey.clear(false, false);
  if (survey.isStartedState) survey.isStartedState = false;
}

/*
 * hideSaveToast
 * The notifier renders its toast only while isDisplayed is true. The timer
 * that showSaveInProgress() starts sets only active and the message, so the
 * toast stays hidden until SurveyJS notifies again.
 */
function hideSaveToast(survey) {
  if (survey.notifier) survey.notifier.isDisplayed = false;
}

/*
 * onPageRestore
 * Runs onRestore once, when the page comes back from the bfcache (a pageshow
 * with persisted set). A fresh load runs no script from before, so it needs
 * nothing.
 */
function onPageRestore(pageWindow, onRestore) {
  if (!pageWindow?.addEventListener) return;
  const onPageShow = (event) => {
    if (!event.persisted) return;
    pageWindow.removeEventListener("pageshow", onPageShow);
    onRestore();
  };
  pageWindow.addEventListener("pageshow", onPageShow);
}

async function clearDraftsSafely(clearDrafts) {
  try {
    await clearDrafts();
  } catch (err) {
    console.error("Could not delete the form drafts", err);
  }
}
